import { Lock, Star, Loader2 } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import type { CalendarChallenge } from '@/pages/GameArchive'

interface ChallengeCellProps {
  challenge: CalendarChallenge
  onClick: (challenge: CalendarChallenge) => void
  isNavigating?: boolean
  signatureColor: string
  challengeType?: 'internal' | 'auto-challenge' | 'external'  // Kept for legend display only
  numberOfStars: number
}

export function ChallengeCell({ challenge, onClick, isNavigating = false, signatureColor, challengeType, numberOfStars }: ChallengeCellProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isClicked, setIsClicked] = useState(false)
  const timeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([])

  // Cleanup all timeouts on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach(clearTimeout)
      timeoutRefs.current = []
    }
  }, [])

  // Helper to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  const isLocked = challenge.displayState === 'locked'

  // Check if challenge should be clickable based on backend displayState
  // Backend gameAccessService is the SINGLE SOURCE OF TRUTH for access decisions
  const isClickable = () => {
    // Respect backend displayState authority
    if (challenge.displayState === 'display-only') return false
    return true // locked (CTA) and playable challenges are all clickable
  }

  // Get challenge state classes using backend displayState
  // Backend gameAccessService determines displayState, frontend only handles presentation
  const getChallengeClasses = () => {
    const baseClasses = 'aspect-square flex flex-col items-center justify-center rounded-xl border-2 transition-all duration-200 relative'
    const clickFeedback = isClicked ? 'scale-95' : ''

    // Disable interaction during loading
    if (isLoading) {
      return `${baseClasses} opacity-50 cursor-wait`
    }

    // Use displayState to determine styling
    // display-only: pale blue, non-clickable (auto-challenge past challenges)
    if (challenge.displayState === 'display-only') {
      return `${baseClasses} bg-blue-50 dark:bg-blue-900/30 border-blue-100 dark:border-blue-800 text-gray-600 dark:text-gray-400 cursor-not-allowed opacity-60`
    }

    // Status-based styling for playable/locked challenges
    switch (challenge.status) {
      case 'today':
        return `${baseClasses} ${clickFeedback} border-[3px] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-[#f8f4f7] dark:hover:bg-gray-600 hover:-translate-y-0.5 hover:shadow-lg active:scale-95`
      case 'completed':
        return `${baseClasses} ${clickFeedback} text-white cursor-pointer hover:-translate-y-0.5 active:scale-95`
      case 'available':
        return `${baseClasses} ${clickFeedback} bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-[#f8f4f7] dark:hover:bg-gray-600 hover:-translate-y-0.5 hover:shadow-md active:scale-95`
      case 'locked':
        return `${baseClasses} bg-gray-100 dark:bg-gray-700/80 border-gray-200 dark:border-gray-500 text-gray-600 dark:text-gray-300 opacity-60 cursor-pointer hover:opacity-80 hover:border-gray-300 dark:hover:border-gray-400 transition-opacity`
      case 'future':
        return `${baseClasses} bg-gray-50 dark:bg-gray-700/50 border-gray-100 dark:border-gray-600 text-gray-400 dark:text-gray-400 opacity-40 cursor-not-allowed`
      default:
        return baseClasses
    }
  }

  // Get dynamic inline styles based on signature color
  const getDynamicStyles = (): React.CSSProperties => {
    switch (challenge.status) {
      case 'today':
        return {
          borderColor: signatureColor,
          boxShadow: `0 0 0 3px ${hexToRgba(signatureColor, 0.1)}`
        }
      case 'completed':
        return {
          backgroundColor: signatureColor,
          borderColor: signatureColor
        }
      default:
        return {}
    }
  }

  // Handle click with loading state
  // Respects backend displayState authority for access decisions
  const handleClick = () => {
    // Respect backend displayState - display-only challenges are not clickable
    if (challenge.displayState === 'display-only') {
      return // Do nothing for display-only challenges
    }

    // Handle locked challenges - show upsell modal
    if (isLocked) {
      onClick(challenge)
      return
    }

    // Handle future challenges - no action (should be disabled in UI)
    if (challenge.status === 'future') {
      return
    }

    // Handle navigable challenges (today, completed, available)
    if (challenge.status === 'today' || challenge.status === 'completed' || challenge.status === 'available') {
      if (isLoading || isNavigating) return // Prevent double-click

      setIsClicked(true)

      // Reset click feedback after animation
      const clickTimer = setTimeout(() => setIsClicked(false), 150)
      timeoutRefs.current.push(clickTimer)

      // Call onClick immediately - parent handles popup or navigation
      onClick(challenge)
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleClick()
    }
  }

  return (
    <div
      className={getChallengeClasses()}
      style={getDynamicStyles()}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={isClickable() && (challenge.status === 'today' || challenge.status === 'completed' || challenge.status === 'available' || isLocked) ? 0 : -1}
      role="button"
      aria-label={`Challenge ${challenge.puzzleNumber}, ${challenge.status}`}
      aria-disabled={isLoading || isLocked || challenge.status === 'future'}
      data-testid={`challenge-cell-${challenge.id}`}
      data-status={challenge.status}
      data-puzzle-number={challenge.puzzleNumber}
    >
      {/* Today badge */}
      {challenge.status === 'today' && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#4CAF50] text-white px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shadow-md z-10"
             style={{ fontFamily: 'Oswald, sans-serif' }}>
          Free Access
        </div>
      )}

      {/* Lock icon for locked challenges */}
      {isLocked && (
        <Lock className="absolute top-2 right-2 w-4 h-4 opacity-50" />
      )}

      {/* Day number - publishDate is already parsed as local midnight in GameArchive */}
      <div className="text-2xl font-bold tracking-wide mb-1"
           style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
        {challenge.publishDate.getDate()}
      </div>

      {/* Challenge number */}
      <div className="text-xs font-medium opacity-70 uppercase tracking-wide"
           style={{ fontFamily: 'Oswald, sans-serif' }}>
        #{challenge.puzzleNumber}
      </div>

      {/* Star rating for completed challenges */}
      {challenge.status === 'completed' && typeof challenge.starsEarned === 'number' && (
        <div className="absolute bottom-2 flex gap-0.5 hidden lg:flex">
          {Array.from({ length: numberOfStars }, (_, i) => i + 1).map(star => (
            <Star
              key={star}
              className={`w-3 h-3 ${star <= challenge.starsEarned! ? 'fill-white text-white' : 'text-white opacity-30'}`}
            />
          ))}
        </div>
      )}

      {/* Loading overlay */}
      {(isLoading || isNavigating) && (
        <div className="absolute inset-0 bg-white/90 dark:bg-gray-800/90 rounded-xl flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: signatureColor }} />
        </div>
      )}
    </div>
  )
}
